import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Search, User } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Startseite", end: true },
  { to: "/galerie", label: "Bilder" },
  { to: "/gedichte", label: "Gedichte" },
  { to: "/ueber-mich", label: "Über mich" },
  { to: "/kontakt", label: "Kontakt" },
];

const slug = (s) => s.toLowerCase().replace(/ü/g, "u").replace(/\s/g, "");

function Wordmark() {
  return (
    <span className="flex items-center gap-2.5">
      <Logo variant="butterfly" className="h-9 sm:h-11 w-auto" />
      <span className="flex flex-col leading-none">
        <span className="font-serif text-2xl sm:text-3xl tracking-[0.2em] text-[#FFF9F2]">GYSUMO</span>
        <span className="hidden sm:block text-[10px] tracking-[0.35em] text-[#F4C542] mt-0.5">KUNST &amp; POESIE</span>
      </span>
    </span>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { customer, logout } = useCustomerAuth();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-[#32104F] transition-shadow duration-300 ${
        scrolled ? "shadow-[0_2px_28px_rgba(0,0,0,0.35)] border-b border-[#F4C542]/25" : "border-b border-[#632A8E]/40"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" aria-label="GYSUMO Startseite">
          <Wordmark />
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${slug(l.label)}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-200 hover:text-[#D85CA4] ${
                  isActive ? "text-[#F4C542] font-medium" : "text-[#FFF9F2]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/suche" data-testid="nav-search" aria-label="Suche" className="text-[#D8B9EA] hover:text-[#F4C542] transition-colors">
            <Search className="w-[18px] h-[18px]" strokeWidth={1.5} />
          </NavLink>
          {customer ? (
            <div className="flex items-center gap-4 pl-2 border-l border-[#632A8E]/50">
              <NavLink to="/konto" data-testid="nav-account" className="flex items-center gap-2 text-sm text-[#FFF9F2] hover:text-[#F4C542] transition-colors">
                <User className="w-[18px] h-[18px]" strokeWidth={1.5} /> {customer.name?.split(" ")[0] || "Konto"}
              </NavLink>
              <button onClick={logout} data-testid="nav-logout" className="text-xs text-[#D8B9EA] hover:text-[#F4C542] transition-colors">Abmelden</button>
            </div>
          ) : (
            <NavLink to="/anmelden" data-testid="nav-login" className="text-sm tracking-wide text-[#F4C542] hover:text-[#D85CA4] transition-colors pl-2 border-l border-[#632A8E]/50">
              Anmelden
            </NavLink>
          )}
        </div>

        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" aria-label="Menü öffnen" className="p-3 -mr-2 text-[#F4C542]">
                <Menu className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#32104F] border-l border-[#632A8E] w-[82vw] max-w-sm">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="mb-8"><Logo variant="butterfly" className="h-14 w-auto" /></div>
              <div className="flex flex-col">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    data-testid={`mobile-nav-${slug(l.label)}`}
                    className={({ isActive }) =>
                      `py-4 text-2xl font-serif border-b border-[#632A8E]/50 transition-colors ${
                        isActive ? "text-[#F4C542]" : "text-[#FFF9F2]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <NavLink to="/suche" data-testid="mobile-nav-search" className="py-4 text-2xl font-serif text-[#FFF9F2]">
                  Suche
                </NavLink>
                {customer ? (
                  <>
                    <NavLink to="/konto" data-testid="mobile-nav-account" className="py-4 text-2xl font-serif text-[#F4C542] border-b border-[#632A8E]/50">
                      Mein Konto
                    </NavLink>
                    <button onClick={() => { logout(); setOpen(false); }} data-testid="mobile-nav-logout" className="py-4 text-2xl font-serif text-[#FFF9F2] text-left">
                      Abmelden
                    </button>
                  </>
                ) : (
                  <NavLink to="/anmelden" data-testid="mobile-nav-login" className="py-4 text-2xl font-serif text-[#F4C542]">
                    Anmelden
                  </NavLink>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
